// Servisfy Status & Updates — Business Web page (universal, all registered Businesses).
// Dedicated page (NOT a popup/modal/overlay). Curated, platform-safe content only (no infra/System-Health).
// FAILS OPEN: a status-feed error renders an honest empty/degraded state and never blocks the portal.
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchStatusUpdates, markStatusSeen, toLocalTime, deriveFeedState, FEED_STATE, fetchStatusUptime } from '../../../lib/statusApi';
import UptimeHistory from './UptimeHistory';

const SEV_STYLE = {
  critical: 'bg-red-100 text-red-800', major: 'bg-orange-100 text-orange-800',
  minor: 'bg-yellow-100 text-yellow-800', info: 'bg-blue-100 text-blue-800',
};

function Badge({ children, className }) {
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${className || 'bg-gray-100 text-gray-700'}`}>{children}</span>;
}

function PostCard({ p }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-start justify-between gap-3">
        <h4 className="font-semibold text-gray-900 text-sm" style={{ wordBreak: 'break-word' }}>{p.title || 'Update'}</h4>
        {p.severity ? <Badge className={SEV_STYLE[p.severity]}>{p.severity}</Badge> : null}
      </div>
      {p.summary ? <p className="text-sm text-gray-700 mt-1">{p.summary}</p> : null}
      {p.details ? <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{p.details}</p> : null}
      {(p.affected_planes && p.affected_planes.length) ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {p.affected_planes.map((pl) => <Badge key={pl}>{pl.replace(/_/g, ' ')}</Badge>)}
        </div>
      ) : null}
      {(p.updates && p.updates.length) ? (
        <ul className="mt-3 border-l-2 border-gray-100 pl-3 space-y-1">
          {p.updates.map((u, i) => (
            <li key={i} className="text-xs text-gray-600">
              <span className="text-gray-400">{toLocalTime(u.at)}</span> — {u.message}
            </li>
          ))}
        </ul>
      ) : null}
      {/* D18-F: server-provided safe action (e.g. a commercial notice → the existing commercial surface).
          Real in-app route only — never a fabricated destination, never a fake success. */}
      {(p.action && p.action.route && p.action.label) ? (
        <div className="mt-3">
          <Link to={p.action.route} className="inline-block text-sm text-purple-700 hover:underline focus:outline-none focus:ring-2 focus:ring-purple-400 rounded">
            {p.action.label} →
          </Link>
        </div>
      ) : null}
      <div className="mt-3 text-xs text-gray-400 flex flex-wrap gap-x-3">
        {p.published_at ? <span>Published {toLocalTime(p.published_at)}</span> : null}
        {p.resolved_at ? <span>Resolved {toLocalTime(p.resolved_at)}</span> : null}
        {p.feature_key ? <span>Feature: {p.feature_key}</span> : null}
        {p.release_reference ? <span>Release: {p.release_reference}</span> : null}
        <span>— {p.author_display || 'Servisfy'}</span>
      </div>
    </div>
  );
}

function Section({ title, posts, empty }) {
  return (
    <section className="mb-6" aria-label={title}>
      <h3 className="text-sm font-semibold text-gray-800 mb-2">{title}</h3>
      {posts.length === 0
        ? <p className="text-sm text-gray-400">{empty}</p>
        : <div className="space-y-3">{posts.map((p) => <PostCard key={p.id} p={p} />)}</div>}
    </section>
  );
}

export default function StatusUpdates() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('ok');   // ok | unauthorized | forbidden | backend_error
  const [partial, setPartial] = useState(false); // backend served but flagged the feed incomplete
  const [live, setLive] = useState([]);        // published (active) posts
  const [history, setHistory] = useState([]);  // resolved/archived
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const feed = await fetchStatusUpdates({ limit: 50, offset: 0, includeHistory: false });
    setLive(feed.items);
    setStatus(feed.status);
    setPartial(feed.partial);
    setLoading(false);
    markStatusSeen(); // opening the page clears the unread dot (best-effort; never blocks)
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadHistory = useCallback(async () => {
    setShowHistory(true);
    const feed = await fetchStatusUpdates({ limit: 50, offset: 0, includeHistory: true });
    // history = resolved/archived only (exclude the still-live ones already shown)
    setHistory(feed.items.filter((p) => ['resolved', 'archived'].includes(p.lifecycle)));
  }, []);

  const buckets = useMemo(() => {
    const b = { current: [], incidents: [], maintenance: [], whats_new: [], release_notes: [], commercial: [] };
    for (const p of live) {
      if (p.type === 'incident') b.incidents.push(p);
      else if (p.type === 'maintenance') b.maintenance.push(p);
      else if (p.type === 'new_feature') b.whats_new.push(p);
      else if (p.type === 'release_note') b.release_notes.push(p);
      else if (p.type === 'commercial') b.commercial.push(p); // D18-F additive commercial notices
      else b.current.push(p); // service_status + announcement
    }
    return b;
  }, [live]);

  // Authoritative platform state + 90-day history. Loaded alongside the feed but kept SEPARATE from
  // it: the feed's emptiness must never imply health, so the banner reads this answer only.
  const [uptime, setUptime] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    fetchStatusUptime({ days: 90 })
      .then((u) => { if (!cancelled) setUptime(u); })
      .catch(() => { if (!cancelled) setUptime({ ok: false, state: 'unknown', days: [] }); });
    return () => { cancelled = true; };
  }, []);

  // Explicit, discriminated feed state — never let a 401/403/5xx or a partial feed look empty or healthy.
  const feedState = deriveFeedState({ loading, status, partial, itemCount: live.length });
  // "degraded" = anything that is not a clean served feed (auth failure / backend error / partial).
  const degraded = ![FEED_STATE.LOADING, FEED_STATE.ACTIVE_CONTENT, FEED_STATE.AUTHENTICATED_EMPTY].includes(feedState);

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto" style={{ overflowY: 'auto' }}
         data-feed-state={feedState} data-degraded={degraded ? 'true' : 'false'}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Status &amp; Updates</h2>
          <p className="text-sm text-gray-500">Platform status, incidents, maintenance and what&apos;s new.</p>
        </div>
        <button
          onClick={load}
          className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-400"
        >Refresh</button>
      </div>

      {/* PARTIAL_CONTENT — served, but the backend flagged the feed incomplete. Show what we have AND say so;
          never present a partial feed as complete or healthy. */}
      {feedState === FEED_STATE.PARTIAL_CONTENT ? (
        <div className="mb-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          Some updates may be missing right now. Your workspace is unaffected.
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : feedState === FEED_STATE.UNAUTHORIZED ? (
        // 401 — NOT an empty feed and NOT a health claim. Ask the user to re-authenticate.
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-3">
          Your session has expired. Please sign in again to view Status &amp; Updates.
        </div>
      ) : feedState === FEED_STATE.FORBIDDEN ? (
        // 403 — authenticated but not permitted. Distinct from empty and from a backend error.
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-3">
          You don&apos;t have access to Status &amp; Updates. Please contact your administrator.
        </div>
      ) : feedState === FEED_STATE.BACKEND_ERROR ? (
        // 5xx / network — the feed served nothing. Honest unavailable state; never "all systems normal".
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-3">
          Status &amp; Updates are temporarily unavailable. Your workspace is unaffected — please try Refresh.
        </div>
      ) : (
        <>
          {/* CURRENT STATE + 90-DAY UPTIME. Added to the EXISTING page above the existing sections;
              nothing below is hidden, renamed or replaced. */}
          <UptimeHistory uptime={uptime} feedDegraded={degraded} />

          {/* AUTHENTICATED_EMPTY of this section means "no current-status notices" — NOT proof of health. */}
          <Section title="Current Status" posts={buckets.current}
                   empty="No current status notices." />
          <Section title="Active Incidents" posts={buckets.incidents} empty="No active incidents." />
          <Section title="Scheduled Maintenance" posts={buckets.maintenance} empty="No scheduled maintenance." />
          <Section title="What's New" posts={buckets.whats_new} empty="No new features yet." />
          {/* D18-F: commercial notices render through the SAME feed/card architecture (additive category). */}
          {buckets.commercial.length ? <Section title="Commercial Updates" posts={buckets.commercial} /> : null}
          <Section title="Release Notes" posts={buckets.release_notes} empty="No release notes yet." />

          <section className="mb-2" aria-label="History">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-800">History</h3>
              {!showHistory ? (
                <button onClick={loadHistory}
                        className="text-sm text-purple-600 hover:underline focus:outline-none focus:ring-2 focus:ring-purple-400">
                  Show history
                </button>
              ) : null}
            </div>
            {showHistory
              ? (history.length === 0
                  ? <p className="text-sm text-gray-400">No past incidents or releases.</p>
                  : <div className="space-y-3">{history.map((p) => <PostCard key={p.id} p={p} />)}</div>)
              : <p className="text-sm text-gray-400">Resolved incidents and past releases.</p>}
          </section>
        </>
      )}
    </div>
  );
}
